/**
 * A pult Zebra nyomtatóinak kezelése (#178): nyomtatónként név és IP-cím
 * (mindkettő helyben szerkeszthető), állapot, beüzemelési útmutató,
 * visszavonás.
 *
 * TOKEN NINCS A FELÜLETEN. A „Beüzemelés" útmutató letöltés gombja a háttérben
 * állítja ki, és a letöltött indítóba írja — a pultosnak nem kell tudnia róla.
 */

import { Badge, Button, Input, Modal, message, Popconfirm, Space, Table, Typography } from 'antd';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  agentState,
  createPrintAgent,
  listPrintAgents,
  type PrintAgent,
  revokePrintAgent,
  rotatePrintAgentToken,
  updatePrintAgent,
} from '../config/labelPrinter';
import { isPrinterHost, PrintAgentGuide } from './PrintAgentGuide';

const { Paragraph, Text } = Typography;

/** A megnyitott útmutató: melyik nyomtatóé, és ha épp most jött létre, a tokenje. */
interface OpenGuide {
  agentId: string;
  freshToken: string | null;
}

interface PrintAgentsModalProps {
  open: boolean;
  eventId: string | undefined;
  onClose: () => void;
  /** Az ügynökök változása után a pult jelzője azonnal frissüljön. */
  onChanged: () => void;
}

export function PrintAgentsModal({
  open,
  eventId,
  onClose,
  onChanged,
}: PrintAgentsModalProps): React.ReactElement {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<PrintAgent[]>([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [creating, setCreating] = useState(false);
  const [guide, setGuide] = useState<OpenGuide | null>(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      setAgents(await listPrintAgents(eventId));
    } catch {
      // A közös interceptor már jelezte a hibát.
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (open) {
      setGuide(null);
      load();
    }
  }, [open, load]);

  const handleCreate = async () => {
    if (!(eventId && name.trim()) || (host.trim() && !isPrinterHost(host))) return;
    setCreating(true);
    try {
      const { agent, token } = await createPrintAgent(eventId, name.trim(), host.trim());
      setGuide({ agentId: agent.id, freshToken: token });
      setName('');
      setHost('');
      await load();
      onChanged();
    } catch {
      // A közös interceptor már jelezte a hibát.
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (
    agent: PrintAgent,
    patch: { name?: string; printerHost?: string },
    successKey: 'printAgents.renamed' | 'printAgents.hostSaved'
  ) => {
    try {
      await updatePrintAgent(agent.id, patch);
      message.success(t(successKey));
      await load();
      // A pult nyomtatóválasztója is az új nevet mutassa, ne 5 másodperc múlva.
      onChanged();
    } catch {
      // A közös interceptor már jelezte a hibát.
    }
  };

  const handleRename = (agent: PrintAgent, value: string) => {
    const next = value.trim();
    if (next && next !== agent.name) handleUpdate(agent, { name: next }, 'printAgents.renamed');
  };

  const handleHost = (agent: PrintAgent, value: string) => {
    const next = value.trim();
    if (next === (agent.printerHost ?? '')) return;
    if (next && !isPrinterHost(next)) {
      message.error(t('printAgents.hostInvalid'));
      return;
    }
    handleUpdate(agent, { printerHost: next }, 'printAgents.hostSaved');
  };

  const handleRevoke = async (agentId: string) => {
    try {
      await revokePrintAgent(agentId);
      if (guide?.agentId === agentId) setGuide(null);
      message.success(t('printAgents.revoked'));
      await load();
      onChanged();
    } catch {
      // A közös interceptor már jelezte a hibát.
    }
  };

  const guideAgent = guide ? agents.find((agent) => agent.id === guide.agentId) : undefined;

  const columns = [
    {
      title: t('printAgents.columnName'),
      dataIndex: 'name',
      key: 'name',
      // Helyben szerkeszthető (ceruza ikon): a név a pult nyomtatóválasztójában
      // jelenik meg, és a gépek választása az azonosítóhoz kötött, tehát
      // átnevezés után sem kell újra választani.
      render: (name: string, agent: PrintAgent) => (
        <Text
          editable={{
            onChange: (value) => handleRename(agent, value),
            maxLength: 100,
            tooltip: t('printAgents.rename'),
            triggerType: ['icon'],
          }}
        >
          {name}
        </Text>
      ),
    },
    {
      title: t('printAgents.columnHost'),
      dataIndex: 'printerHost',
      key: 'printerHost',
      // A router fix IP-je: az indító ebből tudja, hol a nyomtató.
      render: (value: string | null, agent: PrintAgent) => (
        <Text
          type={value ? undefined : 'secondary'}
          editable={{
            onChange: (next) => handleHost(agent, next),
            maxLength: 255,
            tooltip: t('printAgents.editHost'),
            triggerType: ['icon'],
            text: value ?? '',
          }}
        >
          {value || t('printAgents.hostNotSet')}
        </Text>
      ),
    },
    {
      title: t('printAgents.columnState'),
      key: 'state',
      render: (_: unknown, agent: PrintAgent) => {
        const state = agentState(agent);
        const ok = state === 'ready';
        return (
          <Badge
            status={ok ? 'success' : agent.online ? 'warning' : 'default'}
            text={t(`checkin.printerState.${state}`)}
          />
        );
      },
    },
    {
      title: t('printAgents.columnTransport'),
      dataIndex: 'transport',
      key: 'transport',
      render: (value: string | null) => value || '—',
    },
    {
      title: t('printAgents.columnLastSeen'),
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      render: (value: string | null) =>
        value ? new Date(value).toLocaleTimeString() : t('printAgents.never'),
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, agent: PrintAgent) => (
        <Space size={4} wrap>
          <Button
            size="small"
            type={guide?.agentId === agent.id ? 'primary' : 'default'}
            onClick={() => setGuide({ agentId: agent.id, freshToken: null })}
          >
            {t('printAgents.guideButton')}
          </Button>
          <Popconfirm
            title={t('printAgents.revokeConfirm')}
            onConfirm={() => handleRevoke(agent.id)}
            okText={t('printAgents.revoke')}
            cancelText={t('common.cancel')}
          >
            <Button danger size="small">
              {t('printAgents.revoke')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title={t('printAgents.title')}
      onCancel={onClose}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
      width={860}
    >
      <Paragraph type="secondary">{t('printAgents.intro')}</Paragraph>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={agents}
        pagination={false}
        locale={{ emptyText: t('printAgents.empty') }}
        style={{ marginBottom: 16 }}
      />

      <Space.Compact style={{ width: '100%' }}>
        <Input
          value={name}
          maxLength={100}
          placeholder={t('printAgents.namePlaceholder')}
          onChange={(event) => setName(event.target.value)}
          onPressEnter={handleCreate}
          aria-label={t('printAgents.columnName')}
        />
        <Input
          value={host}
          maxLength={255}
          placeholder={t('printAgents.hostPlaceholder')}
          onChange={(event) => setHost(event.target.value)}
          onPressEnter={handleCreate}
          status={host && !isPrinterHost(host) ? 'error' : undefined}
          aria-label={t('printAgents.columnHost')}
          style={{ maxWidth: 220 }}
        />
        <Button
          type="primary"
          loading={creating}
          disabled={!name.trim() || (!!host.trim() && !isPrinterHost(host))}
          onClick={handleCreate}
        >
          {t('printAgents.create')}
        </Button>
      </Space.Compact>

      {guide && (
        <div
          style={{
            marginTop: 16,
            padding: 16,
            border: '1px solid rgba(0, 0, 0, 0.08)',
            borderRadius: 8,
          }}
        >
          {guideAgent && (
            <PrintAgentGuide
              key={guideAgent.id}
              agentName={guideAgent.name}
              printerHost={guideAgent.printerHost || guideAgent.printerStatus?.printerHost}
              freshToken={guide.freshToken}
              onIssueToken={async () => (await rotatePrintAgentToken(guideAgent.id)).token}
              onSaveHost={(value) =>
                handleUpdate(guideAgent, { printerHost: value }, 'printAgents.hostSaved')
              }
            />
          )}
          <Button size="small" onClick={() => setGuide(null)}>
            {t('printAgents.guideClose')}
          </Button>
        </div>
      )}
    </Modal>
  );
}
